
import { ChevronDown, ChevronRight } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AppIdDisplay } from "./AppIdDisplay";
import { type NetworkRule } from "@/hooks/useNetworkRules";

interface GroupedAppRule {
  parentApp: {
    id: string;
    name: string;
    iconUrl?: string;
    bundleId: string;
  };
  rules: NetworkRule[];
}

interface AppSpecificRulesTableProps {
  filteredGroups: any[]; // This will be the app-specific rules
  expandedApps: Set<string>;
  toggleAppExpansion: (bundleId: string) => void;
  onRuleClick: (rule: NetworkRule) => void;
  
}

export const AppSpecificRulesTable = ({ 
  filteredGroups, 
  expandedApps, 
  toggleAppExpansion, 
  onRuleClick,
}: AppSpecificRulesTableProps) => {

  // Group rules by parent app
  const groupedByParentApp = (rules: NetworkRule[]): GroupedAppRule[] => {
    const groupsMap = new Map<string, GroupedAppRule>();

    rules.forEach(rule => {
      if (rule.parentApp) {
        const key = rule.parentApp.id;
        if (!groupsMap.has(key)) {
          groupsMap.set(key, {
            parentApp: rule.parentApp,
            rules: []
          });
        }
        groupsMap.get(key)!.rules.push(rule);
      }
    });

    return Array.from(groupsMap.values());
  };

  // Extract all rules from the filtered groups
  const allRules: NetworkRule[] = [];
  filteredGroups.forEach(group => {
    allRules.push(...group.rules);
  });

  const parentAppGroups = groupedByParentApp(allRules);

  const handleAppToggle = (appId: string) => {
    toggleAppExpansion(appId);
  };
  
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <ScrollArea className="w-full">
        <div className="w-full overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[400px]">App / Rule</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {parentAppGroups.map((group) => (
                <>
                  {/* Parent App Header Row */}
                  <TableRow 
                    key={`app-${group.parentApp.id}`}
                    className="hover:bg-gray-50 cursor-pointer transition-colors bg-gray-25"
                    onClick={() => handleAppToggle(group.parentApp.id)}
                  >
                    <TableCell>
                      <div className="flex items-center">
                        {expandedApps.has(group.parentApp.id) ? (
                          <ChevronDown className="w-4 h-4 mr-2 text-gray-500" />
                        ) : (
                          <ChevronRight className="w-4 h-4 mr-2 text-gray-500" />
                        )}
                        <AppIdDisplay appId={group.parentApp.id} />
                     </div>
                    </TableCell>
                    
                  </TableRow>

                  {/* Expanded Rules */}
                  {expandedApps.has(group.parentApp.id) && group.rules.map((rule) => (
                    <TableRow 
                      key={`rule-${rule.id}`}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => onRuleClick(rule)}
                    >
                      <TableCell>
                        <div className="flex items-center pl-8">
                          <div className="font-medium text-gray-900">{rule.name}</div>
                          {rule.description && (
                            <div className="text-sm text-gray-500 ml-2">- {rule.description}</div>
                          )}
                         </div>
                       </TableCell>
                    </TableRow>
                  ))}
                </>
              ))}
               
                {parentAppGroups.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={1} className="text-center py-8 text-gray-500">
                      No app-specific rules found
                    </TableCell>
                  </TableRow>
                )}
            </TableBody>
          </Table>
        </div>
      </ScrollArea>
    </div>
  );
};
